import React, { useState, useEffect } from 'react';
import { TrendingDown, Clock, Bell, Smartphone, Shield, Zap, DollarSign, Check, ChevronRight, Menu, X, Star, ArrowRight, TrendingUp, AlertCircle, Calendar, BarChart3 } from 'lucide-react';
import './App.css';

const LandingPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'nav-blur shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center glow-purple">
                <TrendingDown className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white">Smart BTC Buyer</span>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-300 hover:text-white transition-all duration-200 hover:scale-105">Features</a>
              <a href="#how-it-works" className="text-gray-300 hover:text-white transition-all duration-200 hover:scale-105">How It Works</a>
              <a href="#pricing" className="text-gray-300 hover:text-white transition-all duration-200 hover:scale-105">Pricing</a>
              <button className="btn-gradient text-white px-6 py-2 rounded-lg font-semibold">
                Get Started
              </button>
            </div>

            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden glass-strong">
            <div className="px-4 pt-2 pb-4 space-y-3">
              <a href="#features" className="block text-gray-300 hover:text-white py-2 transition">Features</a>
              <a href="#how-it-works" className="block text-gray-300 hover:text-white py-2 transition">How It Works</a>
              <a href="#pricing" className="block text-gray-300 hover:text-white py-2 transition">Pricing</a>
              <button className="w-full btn-gradient text-white px-6 py-2 rounded-lg font-semibold">
                Get Started
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Animated Background Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <div className="inline-block mb-4 px-4 py-2 glass rounded-full border border-purple-500/30 shimmer">
                <span className="text-purple-300 font-semibold text-sm">🚀 Save 2-4% on Every Bitcoin Purchase</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6 hero-title">
                Buy Bitcoin at the
                <span className="gradient-text"> Perfect Time</span>
                <br />on Your Vacation
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Don't waste money buying all your Bitcoin at once. Our smart bot checks prices every 6 hours and tells you exactly when to buy.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button className="btn-gradient text-white px-8 py-4 rounded-xl font-bold text-lg glow-hover group">
                  Start Free Trial
                  <ArrowRight className="inline-block ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button className="glass-strong text-white px-8 py-4 rounded-xl font-bold text-lg border-2 border-white/20 hover:border-purple-500/50 transition-all duration-300">
                  Watch Demo
                </button>
              </div>

              <div className="flex items-center space-x-6 text-sm text-gray-400 mb-8">
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>7-day free trial</span>
                </div>
              </div>

              {/* Social Proof */}
              <div className="flex items-center space-x-8 pt-6 border-t border-slate-700/50">
                <div className="flex items-center space-x-2">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 border-2 border-slate-900"></div>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 border-2 border-slate-900"></div>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-400 border-2 border-slate-900"></div>
                  </div>
                  <div>
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">5,000+ happy travelers</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Phone Mockup */}
            <div className="relative">
              <div className="relative mx-auto w-80 h-[600px] bg-slate-800 rounded-[3rem] shadow-2xl p-3 border-8 border-slate-700 phone-float glow-purple">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-40 h-7 bg-slate-700 rounded-b-3xl"></div>
                
                <div className="w-full h-full bg-gradient-to-br from-purple-900 via-slate-900 to-pink-900 rounded-[2.5rem] overflow-hidden p-6">
                  <div className="text-center mb-6 mt-8">
                    <p className="text-purple-300 text-sm mb-2">Current Bitcoin Price</p>
                    <p className="text-white text-4xl font-bold">$42,150</p>
                    <p className="text-green-400 text-sm mt-2">↓ 3.2% below average</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-lg rounded-2xl p-4 border border-green-500/30 mb-4">
                    <p className="text-green-400 text-lg font-bold mb-2">✅ GOOD TIME TO BUY!</p>
                    <p className="text-green-200 text-sm mb-3">Price is below 3-day average</p>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-3 mb-3">
                      <p className="text-green-300 text-xs">Suggested Amount</p>
                      <p className="text-white text-2xl font-bold">$350</p>
                    </div>
                    <button className="w-full bg-white text-green-600 py-3 rounded-xl font-bold hover:bg-green-50 transition">
                      Buy Now
                    </button>
                  </div>
                  
                  <div className="bg-white/5 backdrop-blur rounded-xl p-3 border border-white/10">
                    <div className="flex justify-between text-xs text-gray-300 mb-2">
                      <span>Budget Progress</span>
                      <span>47%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2 mb-2">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full" style={{ width: '47%' }}></div>
                    </div>
                    <p className="text-xs text-gray-400">$700 / $1,500 converted</p>
                  </div>
                </div>
              </div>

              {/* Floating Stats */}
              <div className="absolute -top-10 -right-10 glass-strong rounded-2xl shadow-xl p-4 w-48 border border-purple-500/30 hidden lg:block glow-hover card-hover">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Saved</p>
                    <p className="text-lg font-bold text-green-400 stats-number">$127</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400">vs buying all at once</p>
              </div>

              <div className="absolute -bottom-10 -left-10 glass-strong rounded-2xl shadow-xl p-4 w-48 border border-pink-500/30 hidden lg:block glow-hover card-hover">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Bell className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Alerts Today</p>
                    <p className="text-lg font-bold text-white stats-number">4 times</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400">Every 6 hours</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50 relative">
        <div className="section-divider absolute top-0 left-0 right-0"></div>

        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Everything You Need for <span className="gradient-text">Smart Bitcoin Buying</span>
            </h2>
            <p className="text-xl text-gray-400">Relax on vacation while we find the best prices</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Bell className="w-8 h-8" />,
                title: '6-Hour Price Alerts',
                description: 'Get notified 4 times per day when it\'s a good time to buy. Check your phone 4 times, not 400 times.',
                gradient: 'from-purple-500 to-pink-500'
              },
              {
                icon: <TrendingDown className="w-8 h-8" />,
                title: '3-Day Trend Analysis',
                description: 'We analyze the last 72 hours to tell you if the current price is good compared to the average.',
                gradient: 'from-pink-500 to-red-500'
              },
              {
                icon: <Zap className="w-8 h-8" />,
                title: 'Smart Buy Amounts',
                description: 'We calculate exactly how much to buy based on your budget, days remaining, and price quality.',
                gradient: 'from-blue-500 to-cyan-500'
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: 'Coinbase Integration',
                description: 'Connect your Coinbase account securely. We never hold your funds - you keep full custody.',
                gradient: 'from-green-500 to-emerald-500'
              },
              {
                icon: <Smartphone className="w-8 h-8" />,
                title: 'Budget Tracking',
                description: 'See your progress, average buy price, and how much you\'re beating the market in real-time.',
                gradient: 'from-orange-500 to-amber-500'
              },
              {
                icon: <Clock className="w-8 h-8" />,
                title: 'Vacation Mode',
                description: 'Set notification times, adjust frequency, or pause completely. You control when you check.',
                gradient: 'from-indigo-500 to-purple-500'
              }
            ].map((feature, idx) => (
              <div key={idx} className="glass-strong rounded-2xl p-6 border border-slate-700 hover:border-purple-500/50 card-hover group">
                <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-4 feature-icon`}>
                  {React.cloneElement(feature.icon, { className: 'w-8 h-8 text-white' })}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why DCA Works - New Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="section-divider absolute top-0 left-0 right-0"></div>

        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Why <span className="gradient-text">Dollar-Cost Averaging</span> Beats Lump Sum
            </h2>
            <p className="text-xl text-gray-400">The math behind smart Bitcoin buying during your vacation</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Explanation */}
            <div className="glass-strong rounded-2xl p-8 border border-purple-500/30">
              <h3 className="text-2xl font-bold text-white mb-6">The Problem with Buying All at Once</h3>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                  <span>Bitcoin's price fluctuates <strong className="text-white">constantly</strong> - sometimes 5-10% in a single day</span>
                </p>
                <p className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                  <span>If you buy everything at once, you might catch a <strong className="text-white">temporary peak</strong></span>
                </p>
                <p className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                  <span>You'll spend your entire vacation <strong className="text-white">stressing about the price</strong> instead of relaxing</span>
                </p>
              </div>

              <div className="mt-8 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-300 text-sm">❌ Buy $1,500 all at once when BTC = $45,000</p>
                <p className="text-red-200 font-bold mt-2">Result: 0.0333 BTC at $45,000/BTC</p>
              </div>
            </div>

            {/* Solution */}
            <div className="gradient-border rounded-2xl p-8 glow-hover">
              <h3 className="text-2xl font-bold text-white mb-6">The Smart Way: Dollar-Cost Averaging</h3>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p className="flex items-start space-x-3">
                  <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <span>Buy in <strong className="text-white">small chunks</strong> throughout your 7-day vacation</span>
                </p>
                <p className="flex items-start space-x-3">
                  <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <span>Only buy when price is <strong className="text-white">below the 3-day average</strong></span>
                </p>
                <p className="flex items-start space-x-3">
                  <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <span>Automatically <strong className="text-white">smooth out the volatility</strong> and lower your average cost</span>
                </p>
              </div>

              <div className="mt-8 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                <p className="text-green-300 text-sm">✅ Buy $200-250 when price dips (6-8 purchases)</p>
                <p className="text-green-200 font-bold mt-2">Result: 0.0344 BTC at $43,604/BTC average</p>
                <p className="text-green-400 font-bold text-lg mt-3">💰 You saved $46.50 (3.1%)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real Scenario Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50 relative">
        <div className="section-divider absolute top-0 left-0 right-0"></div>

        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Real <span className="gradient-text">Vacation Scenario</span>
            </h2>
            <p className="text-xl text-gray-400">Meet Sarah - A 7-day vacation in Bali</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Sarah's Profile */}
            <div className="glass-strong rounded-2xl p-8 border border-slate-700">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-2xl">
                  👩‍💼
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Sarah's Plan</h3>
                  <p className="text-gray-400">Software Engineer, 32</p>
                </div>
              </div>

              <div className="space-y-3 text-gray-300">
                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <span className="flex items-center space-x-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    <span>Budget</span>
                  </span>
                  <span className="font-bold text-white">$1,500</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <span className="flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    <span>Vacation Length</span>
                  </span>
                  <span className="font-bold text-white">7 days</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <span className="flex items-center space-x-2">
                    <Clock className="w-5 h-5 text-purple-400" />
                    <span>Check Frequency</span>
                  </span>
                  <span className="font-bold text-white">4x per day</span>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="glass-strong rounded-2xl p-8 border border-slate-700">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
                <BarChart3 className="w-6 h-6 text-purple-400" />
                <span>What Happened (Real BTC Prices)</span>
              </h3>

              <div className="space-y-3">
                {[
                  { day: 'Day 1', time: '2pm', price: 45200, action: '❌ Skip', reason: 'Price high' },
                  { day: 'Day 1', time: '8pm', price: 44800, action: '✅ Buy', amount: 250, reason: 'Dipped!' },
                  { day: 'Day 2', time: '8am', price: 45100, action: '❌ Skip', reason: 'Price up' },
                  { day: 'Day 2', time: '2pm', price: 43900, action: '✅ Buy', amount: 250, reason: 'Great price' },
                  { day: 'Day 3', time: '8am', price: 43600, action: '✅ Buy', amount: 250, reason: 'Even better!' },
                  { day: 'Day 4', time: '2pm', price: 43800, action: '✅ Buy', amount: 250, reason: 'Still good' },
                  { day: 'Day 5', time: '8pm', price: 42900, action: '✅ Buy', amount: 300, reason: 'Big dip!' },
                  { day: 'Day 6', time: '2pm', price: 43500, action: '✅ Buy', amount: 200, reason: 'Final buy' },
                ].map((entry, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${
                    entry.action.includes('✅')
                      ? 'bg-green-500/5 border-green-500/30'
                      : 'bg-slate-800/50 border-slate-700'
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-400">{entry.day} - {entry.time}</span>
                      <span className="text-sm font-mono text-white">${entry.price.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-white">{entry.action}</span>
                      {entry.amount && (
                        <span className="text-sm text-green-400 font-bold">${entry.amount}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{entry.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Results Comparison */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Without Smart BTC Buyer */}
            <div className="bg-red-900/20 border-2 border-red-500/50 rounded-2xl p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Without Smart BTC Buyer</h3>
                  <p className="text-sm text-gray-400">Buy all at once on Day 1</p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount Spent</span>
                  <span className="font-bold text-white">$1,500</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">BTC Price</span>
                  <span className="font-bold text-white">$45,200</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">BTC Received</span>
                  <span className="font-bold text-white">0.03319 BTC</span>
                </div>
              </div>

              <div className="p-4 bg-red-500/20 rounded-xl border border-red-500/30">
                <p className="text-red-300 font-bold text-center">Average Cost: $45,200/BTC</p>
              </div>
            </div>

            {/* With Smart BTC Buyer */}
            <div className="bg-green-900/20 border-2 border-green-500/50 rounded-2xl p-8 glow-hover">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">With Smart BTC Buyer</h3>
                  <p className="text-sm text-gray-400">6 strategic purchases</p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount Spent</span>
                  <span className="font-bold text-white">$1,500</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Average BTC Price</span>
                  <span className="font-bold text-green-400">$43,783</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">BTC Received</span>
                  <span className="font-bold text-green-400">0.03426 BTC</span>
                </div>
              </div>

              <div className="p-4 bg-green-500/20 rounded-xl border border-green-500/30 mb-4">
                <p className="text-green-300 font-bold text-center">Average Cost: $43,783/BTC</p>
              </div>

              <div className="p-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30">
                <div className="text-center">
                  <p className="text-sm text-gray-300 mb-1">Total Savings</p>
                  <p className="text-3xl font-bold text-white mb-1">$48.50</p>
                  <p className="text-green-400 font-bold">3.2% Better</p>
                  <p className="text-xs text-gray-400 mt-2">+ More BTC for same money!</p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Benefits */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="glass rounded-xl p-6 text-center border border-purple-500/30">
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-6 h-6 text-purple-400" />
              </div>
              <h4 className="font-bold text-white mb-2">Less Stress</h4>
              <p className="text-sm text-gray-400">Check 4x/day instead of obsessing every hour</p>
            </div>

            <div className="glass rounded-xl p-6 text-center border border-green-500/30">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-green-400" />
              </div>
              <h4 className="font-bold text-white mb-2">Lower Risk</h4>
              <p className="text-sm text-gray-400">Spread purchases = less volatility risk</p>
            </div>

            <div className="glass rounded-xl p-6 text-center border border-pink-500/30">
              <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-pink-400" />
              </div>
              <h4 className="font-bold text-white mb-2">Fully Automated</h4>
              <p className="text-sm text-gray-400">Auto-buy or approve with one tap</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="section-divider absolute top-0 left-0 right-0"></div>

        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              How It <span className="gradient-text">Works</span>
            </h2>
            <p className="text-xl text-gray-400">Three simple steps to better Bitcoin prices</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Setup Your Budget',
                description: 'Tell us how much Bitcoin you want to buy ($500-$5000) and how many days you\'re on vacation.',
                icon: <DollarSign className="w-6 h-6" />
              },
              {
                step: '2',
                title: 'Connect Coinbase',
                description: 'Securely link your Coinbase account in 30 seconds using OAuth. We never see your password.',
                icon: <Shield className="w-6 h-6" />
              },
              {
                step: '3',
                title: 'Get Alerts & Buy',
                description: 'Receive 4 notifications per day. When price is good, tap "Buy" and we execute on your Coinbase account.',
                icon: <Bell className="w-6 h-6" />
              }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className="gradient-border rounded-2xl p-8 card-hover">
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center font-bold text-white text-xl shadow-lg glow-purple">
                    {item.step}
                  </div>
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4 mt-4 feature-icon">
                    {React.cloneElement(item.icon, { className: 'w-6 h-6 text-purple-400' })}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.description}</p>
                </div>
                {idx < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-purple-500 animate-pulse" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50 relative">
        <div className="section-divider absolute top-0 left-0 right-0"></div>

        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Simple, <span className="gradient-text">Transparent Pricing</span>
            </h2>
            <p className="text-xl text-gray-400">Start free, upgrade when you're ready</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Free',
                price: '$0',
                description: 'Perfect for trying it out',
                features: [
                  'Bitcoin tracking only',
                  '2 alerts per day',
                  'Basic recommendations',
                  'Manual buying',
                  'Ads displayed'
                ],
                cta: 'Start Free',
                popular: false
              },
              {
                name: 'Premium',
                price: '$19.99',
                period: '/month',
                description: 'Best for most vacationers',
                features: [
                  'All cryptocurrencies',
                  '4 alerts per day (every 6 hours)',
                  'Auto-buy feature',
                  'Advanced analytics',
                  'No ads',
                  'Priority support'
                ],
                cta: 'Start Free Trial',
                popular: true
              },
              {
                name: 'Pro',
                price: '$49.99',
                period: '/month',
                description: 'For serious crypto travelers',
                features: [
                  'Everything in Premium',
                  'Multi-exchange support',
                  'Unlimited alerts',
                  'Tax reporting',
                  'Portfolio tracking',
                  'Dedicated support'
                ],
                cta: 'Start Free Trial',
                popular: false
              }
            ].map((plan, idx) => (
              <div key={idx} className={`relative glass-strong rounded-2xl p-8 border card-hover ${plan.popular ? 'pricing-popular border-purple-500 glow-purple' : 'border-slate-700'}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-bold glow-pink">
                    Most Popular
                  </div>
                )}

                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 text-sm mb-6">{plan.description}</p>

                <div className="mb-6">
                  <span className="text-5xl font-bold text-white stats-number">{plan.price}</span>
                  {plan.period && <span className="text-gray-400">{plan.period}</span>}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="flex items-start space-x-2">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button className={`w-full py-3 rounded-xl font-bold transition-all duration-300 group ${
                  plan.popular
                    ? 'btn-gradient text-white glow-hover'
                    : 'glass text-white border border-white/20 hover:border-purple-500/50'
                }`}>
                  {plan.cta}
                  {plan.popular && <ArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="section-divider absolute top-0 left-0 right-0"></div>

        <div className="max-w-4xl mx-auto text-center">
          <div className="gradient-border rounded-3xl p-12 glow-hover relative overflow-hidden">
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 to-pink-900/30 pointer-events-none"></div>

            <div className="relative z-10">
              <h2 className="text-4xl font-bold text-white mb-4">
                Ready to <span className="gradient-text">Save on Your Next Vacation?</span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Join thousands of travelers who are buying Bitcoin smarter. Start your 7-day free trial today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <button className="btn-gradient text-white px-8 py-4 rounded-xl font-bold text-lg glow-hover group">
                  Start Free Trial
                  <ArrowRight className="inline-block ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button className="glass-strong text-white px-8 py-4 rounded-xl font-bold text-lg border-2 border-white/20 hover:border-purple-500/50 transition-all duration-300">
                  Watch Demo
                </button>
              </div>
              <div className="flex items-center justify-center space-x-2 text-gray-400 text-sm">
                <Check className="w-4 h-4 text-green-400" />
                <span>No credit card required</span>
                <span>•</span>
                <Check className="w-4 h-4 text-green-400" />
                <span>7-day free trial</span>
                <span>•</span>
                <Check className="w-4 h-4 text-green-400" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900/80 backdrop-blur-lg border-t border-slate-800 py-12 px-4 sm:px-6 lg:px-8 relative">
        <div className="section-divider absolute top-0 left-0 right-0"></div>

        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center glow-purple">
                  <TrendingDown className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-white">Smart BTC Buyer</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">Buy Bitcoin at the perfect time on your vacation.</p>
            </div>

            <div>
              <h4 className="font-bold text-white mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-400 hover:text-purple-400 transition-colors duration-200">Features</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-purple-400 transition-colors duration-200">Pricing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-purple-400 transition-colors duration-200">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-purple-400 transition-colors duration-200">About</a></li>
                <li><a href="#" className="text-gray-400 hover:text-purple-400 transition-colors duration-200">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-purple-400 transition-colors duration-200">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-purple-400 transition-colors duration-200">Privacy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-purple-400 transition-colors duration-200">Terms</a></li>
                <li><a href="#" className="text-gray-400 hover:text-purple-400 transition-colors duration-200">Security</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2025 Smart BTC Buyer. All rights reserved.</p>
            <p className="mt-2 text-xs opacity-75">Cryptocurrency investments are risky. Only invest what you can afford to lose. Not financial advice.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;